import { useState, useMemo, useEffect, useCallback } from "react";
function useMultiChoiceQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  const [startTime] = useState(() => Date.now());
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const isSolved = solveResult !== null && solveResult !== void 0;
  const answerCount = quiz.answerCount || 1;
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  const sortedAnswers = useMemo(() => {
    if (quiz.random && Array.isArray(quiz.random) && quiz.random.length > 0) {
      return quiz.random.map((idx) => quiz.choices[idx]).filter(Boolean);
    }
    return quiz.choices;
  }, [quiz.choices, quiz.random]);
  const choiceCorrectKeys = useMemo(() => {
    return quiz.choices.filter((a) => a.correct).map((a) => a.key);
  }, [quiz.choices]);
  const resolvedCorrectKeySet = useMemo(() => {
    const set = /* @__PURE__ */ new Set();
    if (!(solveResult == null ? void 0 : solveResult.correctAnswers) || solveResult.correctAnswers.length === 0) {
      choiceCorrectKeys.forEach((k) => set.add(k));
      return set;
    }
    const allChoiceKeys = quiz.choices.map((c) => c.key);
    solveResult.correctAnswers.forEach((ca) => {
      if (allChoiceKeys.includes(ca)) {
        set.add(ca);
        return;
      }
      const posIdx = parseInt(ca, 10);
      if (!isNaN(posIdx)) {
        if (posIdx >= 1 && posIdx <= quiz.choices.length) {
          set.add(quiz.choices[posIdx - 1].key);
        }
      }
    });
    if (set.size === 0) {
      choiceCorrectKeys.forEach((k) => set.add(k));
    }
    return set;
  }, [solveResult == null ? void 0 : solveResult.correctAnswers, quiz.choices, choiceCorrectKeys]);
  const computedInitialKeys = useMemo(() => {
    if (initialAnswersProp && initialAnswersProp.length > 0) {
      return initialAnswersProp;
    }
    if (isViewSolved && (solveResult == null ? void 0 : solveResult.correctAnswers)) {
      return solveResult.correctAnswers;
    }
    return [];
  }, [isViewSolved, solveResult, initialAnswersProp]);
  const [selectedKeys, setSelectedKeys] = useState(computedInitialKeys);
  useEffect(() => {
    setSelectedKeys(computedInitialKeys);
  }, [computedInitialKeys]);
  useEffect(() => {
    onAnswerChange == null ? void 0 : onAnswerChange(selectedKeys);
  }, [selectedKeys, onAnswerChange]);
  const correctsBySelectedKey = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    const submittedKeys = initialAnswersProp ?? selectedKeys;
    if ((solveResult == null ? void 0 : solveResult.corrects) && submittedKeys.length > 0) {
      submittedKeys.forEach((key, idx) => {
        if (idx < solveResult.corrects.length) {
          map.set(key, solveResult.corrects[idx]);
        }
      });
    }
    return map;
  }, [solveResult == null ? void 0 : solveResult.corrects, initialAnswersProp, selectedKeys]);
  const canSubmit = selectedKeys.length > 0;
  const gridContainerId = useMemo(() => `multi-choice-grid-${quizId}`, [quizId]);
  const handleSelect = useCallback((key) => {
    if (isSolved || isViewSolved) return;
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }, [isSolved, isViewSolved]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, selectedKeys, quiz.vars, elapsedTime);
  }, [selectedKeys, canSubmit, isSolved, startTime, onSolve, quizId, quiz.vars]);
  return {
    selectedKeys,
    sortedAnswers,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    answerCount,
    resolvedCorrectKeySet,
    correctsBySelectedKey,
    gridContainerId,
    handleSelect,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  useMultiChoiceQuiz
};
//# sourceMappingURL=use-multi-choice-quiz.js.map
